/**
 * HTTP Client for Storage Adapters
 *
 * Provides HTTP request functionality with:
 * - JWT authentication
 * - Automatic retry with exponential backoff
 * - Error handling and categorization
 *
 * @file HttpClient.ts
 * @module services/storage
 */

/// <reference lib="dom" />

import { StorageError, StorageErrorCode } from './IStorageAdapter'
import type { AuthService } from './factory'

/**
 * Retry configuration for failed HTTP requests
 */
export interface RetryConfig {
  /** Maximum number of retry attempts */
  maxRetries: number
  /** Initial delay in ms */
  initialDelay: number
  /** Maximum delay in ms */
  maxDelay: number
  /** Backoff multiplier */
  backoffMultiplier: number
}

/**
 * Default retry configuration
 */
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 10000,
  backoffMultiplier: 2,
}

/**
 * HTTP Client for storage adapter communication
 *
 * @example
 * ```ts
 * const client = new HttpClient(authService, '/api');
 * const data = await client.fetch<MyData>('/storage/survey/123', { method: 'GET' });
 * ```
 */
export class HttpClient {
  private readonly authService: AuthService
  private readonly baseUrl: string
  private readonly retryConfig: RetryConfig
  private readonly onAuthError: (() => void) | undefined

  /**
   * Create HTTP client
   *
   * @param authService Auth service for JWT tokens
   * @param baseUrl Base URL for API endpoints
   * @param retryConfig Optional retry configuration
   * @param onAuthError Optional callback invoked when authentication fails (e.g. session expired)
   */
  constructor(
    authService: AuthService,
    baseUrl?: string,
    retryConfig?: Partial<RetryConfig>,
    onAuthError?: () => void
  ) {
    this.authService = authService
    // Default to the absolute BFF base (VITE_BFF_URL). A relative '/api' is NOT proxied to the
    // BFF by CloudFront in deployed envs, so it must be the absolute URL like every other service.
    this.baseUrl = baseUrl || (import.meta.env.VITE_BFF_URL as string | undefined) || '/api'
    this.retryConfig = { ...DEFAULT_RETRY_CONFIG, ...retryConfig }
    this.onAuthError = onAuthError
  }

  /**
   * Get authorization headers with JWT token
   *
   * @returns Headers with Authorization and Content-Type
   * @throws {StorageError} If token retrieval fails
   */
  async getAuthHeaders(): Promise<Record<string, string>> {
    try {
      const token = await this.authService.getAccessToken()
      return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    } catch (error) {
      this.onAuthError?.()
      throw new StorageError(
        'Failed to get authentication token',
        StorageErrorCode.AUTH_ERROR,
        error
      )
    }
  }

  /**
   * Execute HTTP request with retry logic
   *
   * Handles:
   * - Auth errors (401/403) - no retry
   * - Conflict errors (409) - no retry
   * - Not found (404) - no retry
   * - Client errors (400-499) - no retry
   * - Server errors (500+) on mutations (POST/PUT/PATCH/DELETE) - no retry (constraint violations are not transient)
   * - Server errors (500+) on reads (GET) - retry with backoff
   * - Network errors on reads - retry with backoff
   * - Network errors on mutations - no retry
   *
   * @param url Request URL
   * @param options Fetch options
   * @param attempt Current attempt number (internal, for network retries)
   * @param rateLimitAttempt Current rate-limit retry count (internal, independent of network retries)
   * @returns Parsed JSON response
   * @throws {StorageError} On HTTP or network errors
   */
  async fetch<R>(url: string, options: RequestInit, attempt = 0, rateLimitAttempt = 0): Promise<R> {
    const method = (options.method ?? 'GET').toUpperCase()
    const isMutation =
      method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE'

    try {
      const response = await fetch(url, options)

      // Handle HTTP errors
      if (!response.ok) {
        // 401 = authentication expired/invalid → trigger re-login. Don't retry.
        if (response.status === 401) {
          this.onAuthError?.()
          throw new StorageError(
            `Authentication failed: ${response.statusText}`,
            StorageErrorCode.AUTH_ERROR
          )
        }

        // 403 = authenticated but NOT permitted (e.g. a view-only share attempting a
        // write). Surface it as a permission error WITHOUT signing the user out — a 403 is
        // not a session expiry. Don't retry.
        if (response.status === 403) {
          throw new StorageError(
            `Permission denied: ${response.statusText}`,
            StorageErrorCode.FORBIDDEN
          )
        }

        // Conflict error - don't retry
        if (response.status === 409) {
          const conflictData = await response.json()
          throw new StorageError(
            'Version conflict detected',
            StorageErrorCode.CONFLICT,
            conflictData
          )
        }

        // Not found - don't retry
        if (response.status === 404) {
          throw new StorageError('Resource not found', StorageErrorCode.NOT_FOUND)
        }

        // Rate limited (429) - retry with Retry-After awareness
        if (response.status === 429) {
          const retryAfterHeader = response.headers.get('Retry-After')
          const retryAfterMs = this.parseRetryAfter(retryAfterHeader)
          const maxRateLimitRetries = 5

          if (rateLimitAttempt < maxRateLimitRetries) {
            const backoffDelay = Math.min(
              this.retryConfig.initialDelay *
                Math.pow(this.retryConfig.backoffMultiplier, rateLimitAttempt),
              this.retryConfig.maxDelay
            )
            const delay = Math.max(retryAfterMs, backoffDelay)
            await new Promise(resolve => setTimeout(resolve, delay))
            // Network retry counter resets; rate-limit counter increments independently
            return this.fetch<R>(url, options, 0, rateLimitAttempt + 1)
          }

          throw new StorageError(
            'Rate limited: too many requests. Please wait and try again.',
            StorageErrorCode.RATE_LIMITED
          )
        }

        // Client errors - don't retry
        if (response.status >= 400 && response.status < 500) {
          let errorMessage = `Client error: ${response.statusText}`
          // Parse structured error bodies (e.g., 422 validation responses)
          try {
            const errorBody = await response.json()
            if (errorBody.detail) {
              errorMessage = errorBody.detail
            } else if (errorBody.error) {
              errorMessage = errorBody.error
            }
          } catch {
            // Failed to parse error body, use status text
          }
          throw new StorageError(errorMessage, StorageErrorCode.INVALID_DATA)
        }

        // Server errors - try to get error details from response body
        let errorDetails = response.statusText
        try {
          const errorBody = await response.json()
          errorDetails = errorBody.message || errorBody.error || JSON.stringify(errorBody)
          if (import.meta.env.DEV) {
            console.error('[HttpClient] Server error details:', errorBody)
          }
        } catch {
          // Failed to parse error body, use status text
        }

        // Mutations: throw StorageError immediately (no retry — constraint violations are not transient)
        if (isMutation) {
          throw new StorageError(
            `Server error: ${response.status} ${errorDetails}`,
            StorageErrorCode.SERVER_ERROR
          )
        }

        // Reads: throw generic Error to trigger retry logic
        throw new Error(`Server error: ${response.status} ${errorDetails}`)
      }

      // Success - parse JSON response (204 No Content has no body)
      if (response.status === 204) {
        return undefined as R
      }
      const data = await response.json()
      return data as R
    } catch (error) {
      // If it's a StorageError, re-throw without retry
      if (error instanceof StorageError) {
        throw error
      }

      // Mutations: do not retry network errors (avoid duplicate side effects)
      if (isMutation) {
        throw new StorageError(
          'Network error: request failed',
          StorageErrorCode.NETWORK_ERROR,
          error
        )
      }

      // Network/server errors on reads - retry with exponential backoff
      if (attempt < this.retryConfig.maxRetries) {
        const delay = Math.min(
          this.retryConfig.initialDelay * Math.pow(this.retryConfig.backoffMultiplier, attempt),
          this.retryConfig.maxDelay
        )

        await new Promise(resolve => setTimeout(resolve, delay))
        return this.fetch<R>(url, options, attempt + 1, rateLimitAttempt)
      }

      // Max retries exceeded
      throw new StorageError(
        'Network error: Max retries exceeded',
        StorageErrorCode.NETWORK_ERROR,
        error
      )
    }
  }

  /**
   * Get base URL
   */
  getBaseUrl(): string {
    return this.baseUrl
  }

  /**
   * Parse Retry-After header value to milliseconds
   *
   * Supports both seconds (integer) and HTTP-date formats.
   * Returns a minimum of 1000ms if header is missing or unparseable.
   */
  private parseRetryAfter(headerValue: string | null): number {
    if (!headerValue) {
      return 1000
    }

    const seconds = Number(headerValue)
    if (!isNaN(seconds) && seconds > 0) {
      return seconds * 1000
    }

    // Try HTTP-date format
    const date = Date.parse(headerValue)
    if (!isNaN(date)) {
      const delayMs = date - Date.now()
      return Math.max(delayMs, 1000)
    }

    return 1000
  }
}
